import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  addNinjaMspConfiguration,
  getNinjaMappedCustemers,
  getNinjaMappedDevices,
  getNinjaMspConfiguration,
  getNinjaOneCustomers,
  getNinjaOneDevices,
  getNinjaOneOAuthAuthorization,
  getNinjaOneOAuthToken,
  mapNinjaOneCustomers,
  mapNinjaOneDevices,
} from "controller/api";
import { ApiErrorType } from "shared";
import {
  MSPConfiguration,
  NinjaCustomerMappingInput,
  NinjaOneDeviceMappingInput,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";

import { debugConsole } from "utilz/helpers";

export const useGetNinjaOneOAuthAuthorization = () => {
  return useMutation({
    mutationFn: (value: { url: string; frontendUrl: string }) =>
      getNinjaOneOAuthAuthorization(value),
    onSuccess: (data) => {
      debugConsole(data);
    },
    onError: (err: ApiErrorType) => {
      debugConsole(err);
      sentryLogger({
        error: err,
        workflow: "integration",
        level: "error",
        functionName: "getNinjaOneOAuthAuthorization",
      });
    },
  });
};

export const userGetNinjaOAuthToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code }: { code: string }) => getNinjaOneOAuthToken({ code }),
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries([
        "ninjaOneOAuthToken",
        "integrationSources",
      ]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "userGetNinjaOAuthToken",
      });
    },
  });
};

export const useGetNinjaOneCustomers = (pageSize: number = 50) => {
  return useInfiniteQuery({
    queryKey: ["ninjaOneCustomers", pageSize],
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      getNinjaOneCustomers({ pageSize, after: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasMore
        ? lastPage?.pagination?.nextAfter ?? undefined
        : undefined;
    },
    onError: (err: ApiErrorType) => {
      debugConsole(err);
      sentryLogger({
        error: err,
        workflow: "integration",
        level: "error",
        functionName: "getNinjaOneCustomers",
      });
    },
  });
};

export const useGetNinjaOneDevices = (
  organizationId: number | undefined,
  pageSize: number = 50,
) => {
  return useInfiniteQuery({
    queryKey: ["ninjaOneDevices", organizationId, pageSize],
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      getNinjaOneDevices({ pageSize, after: pageParam, organizationId }),
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasMore
        ? lastPage?.pagination?.nextAfter ?? undefined
        : undefined;
    },
    enabled: !!organizationId,
    onError: (err: ApiErrorType) => {
      debugConsole(err);
      sentryLogger({
        error: err,
        workflow: "integration",
        level: "error",
        functionName: "getNinjaOneDevices",
      });
    },
  });
};

export const useMapNinjaOneCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: NinjaCustomerMappingInput) => mapNinjaOneCustomers(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ninjaOneCustomers"]);
        queryClient.invalidateQueries(["ninjaIntegrationCustomers", variables.source]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "integration",
          level: "error",
          functionName: "mapNinjaOneCustomers",
        });
      },
    }
  );
};

export const useMapNinjaOneDevices = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: NinjaOneDeviceMappingInput) => mapNinjaOneDevices(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ninjaOneDevices"]);
        queryClient.invalidateQueries(["ninjaIntegrationDevices", variables.source]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "integration",
          level: "error",
          functionName: "mapNinjaOneDevices",
        });
      },
    }
  );
};

export const useGetNinjaMappedCustomers = (source: string) => {
  return useQuery({
    queryKey: ["ninjaIntegrationCustomers", source],
    queryFn: () => getNinjaMappedCustemers(source),
    onError: (err: ApiErrorType) => {
      debugConsole(err);
      sentryLogger({
        error: err,
        workflow: "integration",
        level: "error",
        functionName: "getNinjaMappedCustemers",
      });
    },
  });
};

export const useGetNinjaMappedDevices = (source: string) => {
  return useQuery({
    queryKey: ["ninjaIntegrationDevices", source],
    queryFn: () => getNinjaMappedDevices(source),
    onError: (err: ApiErrorType) => {
      debugConsole(err);
      sentryLogger({
        error: err,
        workflow: "integration",
        level: "error",
        functionName: "getNinjaMappedDevices",
      });
    },
  });
};

export const useAddNinjaMspConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: { board: string }) =>
      addNinjaMspConfiguration(
        value as unknown as { board: MSPConfiguration },
      ),
    {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["mspConfigurations"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "addNinjaMspConfiguration",
      });
    },
  });
};

export const useGetNinjaMspConfigurations = () => {
  return useQuery({
    queryKey: ["mspConfigurations"],
    queryFn: () => getNinjaMspConfiguration(),
    onError: (err: ApiErrorType) => {
      debugConsole(err);
      sentryLogger({
        error: err,
        workflow: "integration",
        level: "error",
        functionName: "getNinjaMspConfiguration",
      });
    },
  });
};
